import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export const STUDENT_HELP_TYPES = [
  'Academic needs',
  'IEP/504 questions',
  'Other',
] as const;

export interface WebsiteStudentHelpRequest {
  id: string;
  help_type: string;
  contact_info: string;
  grade: string;
  subject: string;
  created_at?: string;
  is_read: boolean;
  read_at?: string | null;
}

export type WebsiteStudentHelpRequestUpdate = {
  help_type: string;
  contact_info: string;
  grade: string;
  subject: string;
};

export const useWebsiteStudentHelpRequests = (enabled = true) => {
  return useQuery<WebsiteStudentHelpRequest[]>({
    queryKey: ['websiteStudentHelpRequests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_student_help_requests')
        .select('id, help_type, contact_info, grade, subject, created_at, is_read, read_at')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data as WebsiteStudentHelpRequest[]) ?? [];
    },
    enabled,
    staleTime: 60_000,
  });
};

export const useUpdateWebsiteStudentHelpRequestReadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isRead }: { id: string; isRead: boolean }) => {
      const updates = isRead
        ? { is_read: true, read_at: new Date().toISOString() }
        : { is_read: false, read_at: null };

      const { error } = await supabase
        .from('website_student_help_requests')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websiteStudentHelpRequests'] });
    },
  });
};

export const useUpdateWebsiteStudentHelpRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: WebsiteStudentHelpRequestUpdate;
    }) => {
      const { error } = await supabase
        .from('website_student_help_requests')
        .update({
          help_type: updates.help_type.trim(),
          contact_info: updates.contact_info.trim(),
          grade: updates.grade.trim(),
          subject: updates.subject.trim(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websiteStudentHelpRequests'] });
    },
  });
};
